import type { LocalRow, SeedTables } from '../types/localDb.types'
import { roleId, seedAudit } from './platform.seed'
import { SEED_GROUP, seedId } from './seedId'

/**
 * Konfigurasi Workflow Studio WF-QC-01 (Penugasan QC Karantina RM) — disalin dari
 * `docs/tl-doc/12B-SERVICE-DATA/JAL_12B-SERVICE-DATA-WF-QC-01.md` §1–§8 (label [C]).
 * Tabel email, tabbing, SLA, dan cross-workflow tidak di-seed karena tidak dijalankan local mode.
 *
 * Penyimpangan yang dicatat di laporan C04:
 * - CASL actor operator memakai kolom ERD `operator_id` (TL menulis `assigned_operator_id` yang tidak ada di ERD).
 * - [R] `ACT_QC_SCHEDULE` juga boleh dijalankan `ACT_QC_ADMIN_RM` — BA §IRM-04 "Supervisor, Admin WH RM (schedule)"
 *   dan JAL_04 §4 aturan 6 "Admin may reassign (IRM-04)"; TL 12B §7 hanya mendaftarkan Supervisor.
 *
 * v7.50 (laporan 23 R-04/R-05, keputusan user §9 Q-06/Q-07) — persetujuan hasil QC oleh Admin WH RM:
 * - status baru `QC_PENDING_APPROVAL` "Menunggu Persetujuan" (urutan 3). Ditambahkan di AKHIR daftar supaya id
 *   status lama (dibentuk dari indeks) tidak bergeser; urutan tampil memakai kolom `order`.
 * - `ACT_QC_RECORD` kini `QC_IN_PROGRESS → QC_PENDING_APPROVAL` (draf: hanya angka Good/NG + kode yang dipesan).
 * - `ACT_QC_APPROVE_PRINT` (`QC_PENDING_APPROVAL → QC_DONE`, Admin WH RM dan Supervisor WH RM — keputusan 2026-10-21 #2): "Setujui & Cetak Label" — tanpa status
 *   "Disetujui" di antaranya (Q-07). `ACT_QC_RETURN` (`QC_PENDING_APPROVAL → QC_IN_PROGRESS`, alasan wajib).
 * - Persetujuan per pallet (satu paket = seluruh assignment satu pallet yang disimpan operator yang sama).
 */

const WORKFLOW_NO = 3
export const WF_QC_01_ID = seedId(SEED_GROUP.workflow, WORKFLOW_NO)

/** Rentang id per workflow dipisah ribuan agar tidak bertabrakan dengan WF-PRC-01 / WF-TOB-01. */
const scoped = (n: number) => WORKFLOW_NO * 1000 + n

const STATUSES = [
  ['QC_ASSIGNED', 'Assigned', 1, true, false, '#3B82F6', 'Penugasan QC dijadwalkan oleh Supervisor RM'],
  ['QC_IN_PROGRESS', 'In Progress', 2, false, false, '#F59E0B', 'Operator memindai QR Pallet dan memulai inspeksi QC'],
  ['QC_DONE', 'QC Done', 4, false, false, '#8B5CF6', 'Hasil QC disetujui Admin — unit, label & putaway terbit'],
  ['QC_IN_STORAGE', 'In Storage', 5, false, true, '#10B981', 'Seluruh unit hasil QC selesai ditaruh di rak'],
  [
    'QC_PENDING_APPROVAL',
    'Menunggu Persetujuan',
    3,
    false,
    false,
    '#A855F7',
    'Hasil QC disimpan operator — menunggu Admin WH RM menyetujui & mencetak label'
  ]
] as const

export type QcStatusCode = (typeof STATUSES)[number][0]

export const qcStatusId = (code: QcStatusCode) =>
  seedId(SEED_GROUP.wmlStatus, scoped(STATUSES.findIndex(([c]) => c === code) + 1))

const ACTORS = [
  ['ACT_QC_SPV_RM', 'WH RM Supervisor', 'wh_rm_supervisor', null],
  ['ACT_QC_OP_RM', 'Operator WH RM', 'operator_wh_rm', { operator_id: { _eq: '$CURRENT_USER.id' } }],
  ['ACT_QC_ADMIN_RM', 'Admin WH RM', 'admin_wh_rm', null]
] as const

export type QcActorCode = (typeof ACTORS)[number][0]

export const qcActorId = (code: QcActorCode) =>
  seedId(SEED_GROUP.wmlActor, scoped(ACTORS.findIndex(([c]) => c === code) + 1))

const COMPONENTS = [
  ['CMP_QC_ASSIGN', 'Tombol Jadwalkan QC', 'ACTION', { color: '#3B82F6', variant: 'primary' }],
  ['CMP_QC_START_SCAN', 'Tombol Mulai Scan Pallet', 'ACTION', {}],
  ['CMP_QC_SUBMIT_RESULT', 'Tombol Simpan Hasil QC', 'ACTION', {}],
  ['CMP_QC_PUTAWAY_FINISH', 'Tombol Konfirmasi Putaway Selesai', 'ACTION', {}],
  ['CMP_QC_PANEL_HEADER', 'Panel Header Penugasan QC', 'PANEL', {}],
  ['CMP_QC_PANEL_INSPECTION', 'Panel Form Hasil Inspeksi & Alasan NG', 'PANEL', {}],
  ['CMP_QC_APPROVE_PRINT', 'Tombol Setujui & Cetak Label', 'ACTION', { color: '#3B82F6', variant: 'primary' }],
  ['CMP_QC_RETURN', 'Tombol Kembalikan ke Operator', 'ACTION', { color: '#EF4444', variant: 'outlined' }]
] as const

type ComponentCode = (typeof COMPONENTS)[number][0]

const componentId = (code: ComponentCode) =>
  seedId(SEED_GROUP.wmlComponent, scoped(COMPONENTS.findIndex(([c]) => c === code) + 1))

/**
 * [name, from, to, component, entity_patch_mapping, reason wajib] — TL 12B §6 + v7.50 (R-04/R-05).
 * Hanya `ACT_QC_RETURN` yang butuh alasan (prototype prc-05 `kembalikanQc`).
 */
const ACTIONS: [string, QcStatusCode, QcStatusCode, ComponentCode, Record<string, unknown> | null, boolean][] = [
  ['ACT_QC_SCHEDULE', 'QC_ASSIGNED', 'QC_ASSIGNED', 'CMP_QC_ASSIGN', null, false],
  ['ACT_QC_START', 'QC_ASSIGNED', 'QC_IN_PROGRESS', 'CMP_QC_START_SCAN', null, false],
  ['ACT_QC_RECORD', 'QC_IN_PROGRESS', 'QC_PENDING_APPROVAL', 'CMP_QC_SUBMIT_RESULT', null, false],
  [
    'ACT_QC_STORE_ALL',
    'QC_DONE',
    'QC_IN_STORAGE',
    'CMP_QC_PUTAWAY_FINISH',
    { is_stored: true, stored_at: '$NOW' },
    false
  ],
  /* Penyetuju & waktunya DERIVE dari `wxl_submission_log` (pola D10) — tidak ada kolom approved_* di ERD. */
  ['ACT_QC_APPROVE_PRINT', 'QC_PENDING_APPROVAL', 'QC_DONE', 'CMP_QC_APPROVE_PRINT', null, false],
  ['ACT_QC_RETURN', 'QC_PENDING_APPROVAL', 'QC_IN_PROGRESS', 'CMP_QC_RETURN', null, true]
]

export type QcActionName =
  | 'ACT_QC_SCHEDULE'
  | 'ACT_QC_START'
  | 'ACT_QC_RECORD'
  | 'ACT_QC_STORE_ALL'
  | 'ACT_QC_APPROVE_PRINT'
  | 'ACT_QC_RETURN'

export const qcActionId = (name: QcActionName) =>
  seedId(SEED_GROUP.wmlAction, scoped(ACTIONS.findIndex(([n]) => n === name) + 1))

const ELIGIBILITY: [QcActionName, QcActorCode][] = [
  ['ACT_QC_SCHEDULE', 'ACT_QC_SPV_RM'],
  ['ACT_QC_SCHEDULE', 'ACT_QC_ADMIN_RM'],
  ['ACT_QC_START', 'ACT_QC_OP_RM'],
  ['ACT_QC_RECORD', 'ACT_QC_OP_RM'],
  ['ACT_QC_STORE_ALL', 'ACT_QC_OP_RM'],
  ['ACT_QC_APPROVE_PRINT', 'ACT_QC_ADMIN_RM'],
  ['ACT_QC_RETURN', 'ACT_QC_ADMIN_RM'],
  /* Keputusan user 2026-10-21 #2: Supervisor WH RM juga menyetujui & mengembalikan hasil QC (Batch A: Admin saja). */
  ['ACT_QC_APPROVE_PRINT', 'ACT_QC_SPV_RM'],
  ['ACT_QC_RETURN', 'ACT_QC_SPV_RM']
]

/** [komponen panel, status, permission, actor]. Baris TL tanpa actor → [A] semua actor WF-QC-01 (pola WF-TOB-01). */
const PANELS: [ComponentCode, QcStatusCode, 'read' | 'write', QcActorCode | null][] = [
  ['CMP_QC_PANEL_HEADER', 'QC_ASSIGNED', 'read', 'ACT_QC_SPV_RM'],
  ['CMP_QC_PANEL_HEADER', 'QC_IN_STORAGE', 'read', null],
  ['CMP_QC_PANEL_INSPECTION', 'QC_ASSIGNED', 'read', null],
  ['CMP_QC_PANEL_INSPECTION', 'QC_IN_PROGRESS', 'write', 'ACT_QC_OP_RM'],
  ['CMP_QC_PANEL_INSPECTION', 'QC_DONE', 'read', null],
  /* v7.50: draf hasil QC terbaca semua actor; Admin dan Supervisor WH RM memutuskan (keputusan 2026-10-21 #2), Kadiv lihat saja. */
  ['CMP_QC_PANEL_INSPECTION', 'QC_PENDING_APPROVAL', 'read', null]
]

/** Aksi yang mengantar penugasan ke status tertentu — dipakai seed untuk menyusun log submission. */
export const QC_ACTION_PATH: Record<QcStatusCode, QcActionName[]> = {
  QC_ASSIGNED: ['ACT_QC_SCHEDULE'],
  QC_IN_PROGRESS: ['ACT_QC_SCHEDULE', 'ACT_QC_START'],
  QC_PENDING_APPROVAL: ['ACT_QC_SCHEDULE', 'ACT_QC_START', 'ACT_QC_RECORD'],
  QC_DONE: ['ACT_QC_SCHEDULE', 'ACT_QC_START', 'ACT_QC_RECORD', 'ACT_QC_APPROVE_PRINT'],
  QC_IN_STORAGE: ['ACT_QC_SCHEDULE', 'ACT_QC_START', 'ACT_QC_RECORD', 'ACT_QC_APPROVE_PRINT', 'ACT_QC_STORE_ALL']
}

export const QC_ACTION_STATUS: Record<QcActionName, [QcStatusCode, QcStatusCode]> = {
  ACT_QC_SCHEDULE: ['QC_ASSIGNED', 'QC_ASSIGNED'],
  ACT_QC_START: ['QC_ASSIGNED', 'QC_IN_PROGRESS'],
  ACT_QC_RECORD: ['QC_IN_PROGRESS', 'QC_PENDING_APPROVAL'],
  ACT_QC_STORE_ALL: ['QC_DONE', 'QC_IN_STORAGE'],
  ACT_QC_APPROVE_PRINT: ['QC_PENDING_APPROVAL', 'QC_DONE'],
  ACT_QC_RETURN: ['QC_PENDING_APPROVAL', 'QC_IN_PROGRESS']
}

export function buildWorkflowQcSeed(): SeedTables {
  const panels: LocalRow[] = PANELS.map(([component, status, permission], index) => ({
    ...seedAudit,
    id: seedId(SEED_GROUP.wmlPanel, scoped(index + 1)),
    component_id: componentId(component),
    status_id: qcStatusId(status),
    permission
  }))

  return {
    wml_workflow: [
      {
        ...seedAudit,
        id: WF_QC_01_ID,
        code: 'WF-QC-01',
        name: 'Inbound QC Assignment RM Workflow',
        entity: 'rm_qc_assignment',
        source_policy: 'manual_create'
      }
    ],
    wml_status: STATUSES.map(([code, name, order, isStart, isTerminal, color, desc], index) => ({
      ...seedAudit,
      id: seedId(SEED_GROUP.wmlStatus, scoped(index + 1)),
      workflow_id: WF_QC_01_ID,
      code,
      name,
      order,
      is_start: isStart,
      is_terminal: isTerminal,
      is_send_email: code === 'QC_DONE',
      color,
      desc
    })),
    wml_actor: ACTORS.map(([code, name, role, condition]) => ({
      ...seedAudit,
      id: qcActorId(code),
      workflow_id: WF_QC_01_ID,
      code,
      name,
      role_id: roleId(role),
      job_fn_id: null,
      condition
    })),
    wml_component: COMPONENTS.map(([code, name, type, uiConfig]) => ({
      ...seedAudit,
      id: componentId(code),
      workflow_id: WF_QC_01_ID,
      code,
      name,
      type,
      ui_config: uiConfig
    })),
    wml_action: ACTIONS.map(([name, from, to, component, patch, needReason]) => ({
      ...seedAudit,
      id: qcActionId(name as QcActionName),
      workflow_id: WF_QC_01_ID,
      name,
      from_status_id: qcStatusId(from),
      to_status_id: qcStatusId(to),
      component_id: componentId(component),
      is_need_reason: needReason,
      is_reason_required: needReason,
      is_post_closure: false,
      entity_patch_mapping: patch
    })),
    wml_action_actor_eligibility: ELIGIBILITY.map(([action, actor], index) => ({
      ...seedAudit,
      id: seedId(SEED_GROUP.wmlActionActor, scoped(index + 1)),
      action_id: qcActionId(action),
      actor_id: qcActorId(actor)
    })),
    wml_panel: panels,
    wml_panel_actor_eligibility: PANELS.flatMap(([, , , actor], index) =>
      (actor ? [actor] : ACTORS.map(([code]) => code)).map((actorCode, offset) => ({
        ...seedAudit,
        id: seedId(SEED_GROUP.wmlPanelActor, scoped((index + 1) * 10 + offset)),
        panel_id: panels[index].id,
        actor_id: qcActorId(actorCode)
      }))
    )
  }
}
